import clsx from 'clsx';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useStore } from 'react-redux';
import { useLocation } from 'react-router';

import { IState, useAppSelector } from 'appRedux/createStore';
import { ICasinoSchema } from 'appRedux/models/casinoModels';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import LinkPlayerCardModal from 'pages/MyPlayerCards/LinkPlayerCardModal';
import {
  LINK_PLAYER_DUE_EVENT,
  LinkPlayerDrawerFrame,
  clearLinkPlayerDue,
  computeLinkPlayerDrawerFrame,
  isExplorePath,
  isLinkedAndVerifiedFor,
  peekLinkPlayerDue,
  shouldOpenLinkPlayerDrawer,
} from 'lib/linkPlayerFlow';
import {
  getRandomOutcomesActive,
  hasShownRandomOutcomesThisSession,
  peekLoginCasinoReveal,
  subscribeRandomOutcomesActive,
} from 'lib/loginCasinoReveal';

/**
 * Link Player ID in the Explore drawer (casino LinkPlayerflowVersion 2).
 *
 * Mounted by Home next to LoginCasinoRevealHost. ChooseCasinos leaves a one-time
 * "Link Player due" note for a Version 2 casino entry by a player who isn't linked and
 * verified there (instead of the full-screen /auth/link-player step) and opens Explore.
 * This host opens the drawer once for that entry:
 *   - after Random Outcomes (never together): it waits while Random Outcomes is due or
 *     on screen, so on the first entry of a login session it opens after Accept;
 *   - the note is used up when the drawer opens, so moving between tabs, coming back to
 *     Explore or changing insight/timeframe never reopens it. MAYBE LATER closes it
 *     until the next casino entry.
 *
 * The drawer stops at the bottom of the casino header (never over it), sits above the
 * tab bar (and the keyboard when it is up), and its content scrolls inside. It reuses
 * LinkPlayerCardModal (variant 'drawer') for the form, copy and submit logic, without
 * the casino logo/tag. Explore and its EGM data load underneath while it is open; this
 * component touches no data path.
 */

const HEADER_SELECTOR = '.slotcheck-header';
const TAB_BAR_SELECTOR = 'ion-tab-bar';
const CLOSE_MS = 280;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(REDUCED_MOTION_QUERY).matches
    );
  } catch {
    return false;
  }
}

/** Bottom of the visible casino header / top of the visible tab bar, in px. */
function visibleEdge(selector: string, edge: 'top' | 'bottom'): number | null {
  if (typeof document === 'undefined') return null;
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(selector));
  for (const el of nodes) {
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.width > 0) return edge === 'top' ? r.top : r.bottom;
  }
  return null;
}

function measureFrame(): LinkPlayerDrawerFrame {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  return computeLinkPlayerDrawerFrame({
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    headerBottom: visibleEdge(HEADER_SELECTOR, 'bottom'),
    tabBarTop: visibleEdge(TAB_BAR_SELECTOR, 'top'),
    visualViewportBottom: vv ? vv.offsetTop + vv.height : null,
  });
}

function useRandomOutcomesActive(): boolean {
  const [active, setActive] = useState(getRandomOutcomesActive);
  useEffect(() => {
    setActive(getRandomOutcomesActive());
    return subscribeRandomOutcomesActive(() => setActive(getRandomOutcomesActive()));
  }, []);
  return active;
}

export const LinkPlayerDrawer: React.FC<{ casinoInfo: ICasinoSchema; onClose: () => void }> = ({
  casinoInfo,
  onClose,
}) => {
  const dispatch = useDispatch();
  const store = useStore<IState>();
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const [frame, setFrame] = useState<LinkPlayerDrawerFrame | null>(null);
  const [closing, setClosing] = useState(false);
  const [reducedMotion] = useState(prefersReducedMotion);
  const closeTimer = useRef<number | null>(null);

  // Frame: re-measured on resize, orientation, the keyboard (visual viewport) and fonts.
  useLayoutEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      setFrame((prev) => {
        const next = measureFrame();
        return prev &&
          prev.top === next.top &&
          prev.bottom === next.bottom &&
          prev.height === next.height
          ? prev
          : next;
      });
    };
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', schedule);
    visualViewport?.addEventListener('scroll', schedule);
    document.fonts?.ready.then(schedule).catch(() => undefined);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      visualViewport?.removeEventListener('resize', schedule);
      visualViewport?.removeEventListener('scroll', schedule);
    };
  }, []);

  useEffect(
    () => () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    },
    []
  );

  const close = useCallback(() => {
    if (closeTimer.current) return;
    setClosing(true);
    closeTimer.current = window.setTimeout(onClose, reducedMotion ? 0 : CLOSE_MS);
  }, [onClose, reducedMotion]);

  // Android hardware back: same as MAYBE LATER.
  useEffect(() => {
    const onBack = (ev: Event) => {
      const detail = (ev as CustomEvent<{ register?: (priority: number, cb: () => void) => void }>)
        .detail;
      detail?.register?.(100000, close);
    };
    document.addEventListener('ionBackButton', onBack);
    return () => document.removeEventListener('ionBackButton', onBack);
  }, [close]);

  // Linked: same linked-state update the full-screen step does after Continue.
  const handleSubmitSuccess = () => {
    const linkedAccounts =
      store.getState().app.persistedState.linkPlayerState?.casinoLinkedAccounts ?? [];
    const casinoItem = linkedAccounts.find((item) => item.casinoId === casinoInfo.casinoId);
    casinoCommands.SetLinkedCasinos({
      casinoLinkedAccounts: linkedAccounts,
      isPlayerLinked: Boolean(casinoItem),
      linkedCasinoId: casinoItem ? casinoItem.casinoId : null,
    });
    close();
  };

  const style = frame
    ? ({
        '--lp-drawer-top': `${frame.top}px`,
        '--lp-drawer-bottom': `${frame.bottom}px`,
      } as React.CSSProperties)
    : undefined;

  return ReactDOM.createPortal(
    <div className={clsx('lp-drawer-root', closing && 'lp-drawer-root--closing')} style={style}>
      {/* Dims the map only (below the header, above the tab bar); taps do nothing. */}
      <div className="lp-drawer__backdrop" aria-hidden="true" />
      <div
        className={clsx('lp-drawer', closing && 'lp-drawer--closing')}
        role="dialog"
        aria-modal="true"
        aria-label="Link Player ID"
        data-testid="linkPlayerDrawer"
      >
        <div className="lp-drawer__handle" aria-hidden="true">
          <span className="lp-drawer__handle-bar" />
        </div>
        <div className="lp-drawer__scroll">
          <LinkPlayerCardModal
            variant="drawer"
            casinoInfo={casinoInfo}
            onSubmitSuccess={handleSubmitSuccess}
            onCancel={close}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export const LinkPlayerDrawerHost: React.FC = () => {
  const location = useLocation();
  const casinoSchema = useAppSelector((state) => state.app.persistedState.casinoSchema);
  const loginKey = useAppSelector((state) => state.app.persistedState.userInfo?.appUserPk);
  const isCasinoAccount = useAppSelector(
    (state) => state.app.persistedState.userInfo?.isCasinoAccount
  );
  const linkedAccounts = useAppSelector(
    (state) => state.app.persistedState.linkPlayerState?.casinoLinkedAccounts
  );
  const randomOutcomesActive = useRandomOutcomesActive();
  const [dueTick, setDueTick] = useState(0);
  const [open, setOpen] = useState<{ entry: string; casinoId: number } | null>(null);
  const onExplore = isExplorePath(location.pathname);

  useEffect(() => {
    const onDue = () => setDueTick((t) => t + 1);
    window.addEventListener(LINK_PLAYER_DUE_EVENT, onDue);
    return () => window.removeEventListener(LINK_PLAYER_DUE_EVENT, onDue);
  }, []);

  useEffect(() => {
    if (open || !onExplore) return;
    const due = peekLinkPlayerDue();
    const randomOutcomesPending =
      peekLoginCasinoReveal() === 'drawer' && !hasShownRandomOutcomesThisSession(loginKey);
    if (
      due &&
      shouldOpenLinkPlayerDrawer({
        due,
        casino: casinoSchema,
        randomOutcomesPending,
        randomOutcomesActive,
        isCasinoAccount: Boolean(isCasinoAccount),
        isLinkedAndVerified: isLinkedAndVerifiedFor(linkedAccounts, casinoSchema?.casinoId),
      })
    ) {
      // Used up now: it opens once per casino entry, never again while staying here.
      clearLinkPlayerDue();
      setOpen({ entry: due.entry, casinoId: due.casinoId });
    }
  }, [
    open,
    onExplore,
    location.key,
    dueTick,
    randomOutcomesActive,
    casinoSchema,
    loginKey,
    isCasinoAccount,
    linkedAccounts,
  ]);

  // Leaving Explore (a tab, Select Casino, logout) or a different casino closes it like
  // MAYBE LATER; it only comes back on the next casino entry.
  const stale = Boolean(open) && (!onExplore || Number(casinoSchema?.casinoId) !== open?.casinoId);
  useEffect(() => {
    if (stale) setOpen(null);
  }, [stale]);

  const handleClose = useCallback(() => setOpen(null), []);

  if (!open || stale || !casinoSchema) return null;
  return <LinkPlayerDrawer key={open.entry} casinoInfo={casinoSchema} onClose={handleClose} />;
};

export default LinkPlayerDrawerHost;
